<template>
  <div id="fusion_toolbar" class="flex h-full">
    <!-- Left Column - Responsive -->
    <div class="flex-grow left-column overflow-hidden" :class="fusion_collapsed?'fusion-collapsed':''">
      <!-- Loading state. [NPM-72] 'connecting' is loading with a soft hint: FusionMain flips to it
           once retry polling has run a few seconds, so a slow host app is not a bare spinner. -->
      <div v-if="authState === 'loading' || authState === 'connecting'" class="flex flex-col items-center justify-center w-full h-full gap-3" style="min-height: 300px;">
        <div class="fusion-loader" />
        <span v-if="authState === 'connecting'" class="text-sm text-text-subtle select-none">{{ $t('common.connecting') }}</span>
      </div>
      <!-- Unauthorized state -->
      <div v-else-if="authState === 'unauthorized'" class="w-full">
        <p class="w-full text-center text-text-default py-5 select-none" style="min-height: 300px" @click="handleUnauthorizedClick">{{ $FUSunauthorizedMessage }}</p>
      </div>
      <!-- Ready state -->
      <template v-else>
        <div class="w-full h-full items-center justify-center" :class="active_tab === 'Reynolds' ? 'flex' : 'hidden'">
          <div class="flex flex-col items-center justify-center w-full">
            <div  class="mb-3 mx-auto cursor-pointer w-full" @click="sent_message=null" style="min-height: 100px; max-width: 90%" :style="`visibility: ${sent_message?'visible':'hidden'}`">
              <h6 class="mb-1 text-text-default">{{ $t('toolbar.debug.sentMessage') }}</h6>
              <textarea class="text-text-default">{{ JSON.stringify(sent_message, null, 2) }}</textarea>
            </div>
            <span v-html="rr_logo" class="cursor-pointer text-icon-fill w-16 inline-block rr-logo" @click="sendWindowMessage('REQUEST-CONTEXT')" />
            <div class="mt-3 mx-auto cursor-pointer w-full" @click="received_message=null" style="min-height: 100px; max-width: 90%" :style="`visibility: ${received_message?'visible':'hidden'}`">
              <h6 class="mb-1 text-text-default">{{ $t('toolbar.debug.receivedMessage') }}</h6>
              <textarea class="text-text-default">{{ JSON.stringify(received_message, null, 2) }}</textarea>
            </div>
          </div>
        </div>
        <!-- Unentitled (no FrontRunner/REYUSER): the server 403s the Rey thread, so show the
             not-authorized message rather than "Unable to Load Spark". -->
        <div v-if="active_tab === 'Spark' && !has_fusion_entitlement" class="w-full">
          <p class="w-full text-center text-text-default py-5 select-none" style="min-height: 300px" @click="handleUnauthorizedClick">{{ $FUSunauthorizedMessage }}</p>
        </div>
        <div v-else-if="active_tab === 'Spark' && mainStore.ai_thread_loading" class="flex items-center justify-center w-full h-full" style="min-height: 300px;">
          <div class="fusion-loader" />
        </div>
        <div v-else-if="active_tab === 'Spark' && !spark_thread_id">
          <p class="w-full text-center text-text-default py-5" style="min-height: 300px">{{ $t('toolbar.unableToLoadSpark') }}</p>
        </div>
        <fusion-body v-if="has_spark && has_fusion_entitlement && spark_thread_id" v-show="active_tab === 'Spark'" key="toolbar_spark" :set-configuration="spark_body_config"
          :hide-options-menu="true" />
        <fusion-body v-if="has_contact" v-show="active_tab === 'Contact'" key="toolbar_contact" :set-configuration="contact_body_config"
          :hide-options-menu="true" />
        <fusion-body v-if="has_conversations" v-show="active_tab === 'Conversations'" key="toolbar_conversations" :set-configuration="conversations_body_config"
          :hide-options-menu="true" />
        <fusion-body v-if="has_internal_messaging" v-show="active_tab === 'InternalMessaging'" key="toolbar_internal_messaging" :set-configuration="internal_messaging_body_config"
          :hide-options-menu="true" />
        <iframe v-if="has_autovision" v-show="active_tab === 'AutoVision'" :key="iframe_key_autovision" :src="autovision_iframe_url" class="w-full h-full border-0" />
      </template>
      <!-- OptionsMenu lives inside left-column so its DialogModal (position:fixed) is scoped
           to this panel by the transform:translateZ(0) on .left-column, preventing it from
           covering the right-column taskbar in ERA / iframe contexts. -->
      <options-menu v-if="user_access_info" :trigger-show-options="options_menu_trigger" @updateShowOptions="updateShowOptions" @openReyConversation="openReyConversation" />
      <!-- Incoming internal-message toast. Rendered inside .left-column so its position:fixed is
           scoped to this panel (via the column's transform:translateZ(0)), keeping it clear of the
           right-column tab rail. Click and close both just dismiss. -->
      <message-toast :message="toast_message" @close="dismissMessageToast" @click="dismissMessageToast" />
    </div>

    <!-- Right Column - Fixed Width -->
    <div class="right-column py-3">
      <div class="flex flex-col items-center justify-start h-full pt-3">
        <div v-if="show_close" class="p-2 cursor-pointer" :title="$t('toolbar.collapse')" @click="expandCollapse(true)">
          <i class="ri-close-line ri-lg" />
        </div>
        <template v-else>
          <div class="p-2 cursor-pointer" @click="setActiveTab('Reynolds')">
            <span v-html="rr_logo_icon" class="text-icon-fill w-[22px] inline-block rr-logo" />
          </div>
          <!-- Collapse/expand chevron. Hidden for hosts that cannot collapse (see can_collapse) so
               they never get an affordance that would leave the rail stranded in a full-width frame. -->
          <div v-if="can_collapse" class="px-2 cursor-pointer text-icon-fill" :title="fusion_collapsed ? $t('toolbar.expand') : $t('toolbar.collapse')" @click="expandCollapse(!Boolean(fusion_collapsed))">
            <span class="inline-flex" v-html="fusion_collapsed?expand_icon:collapse_icon" />
          </div>
        </template>
        <!-- Section divider (header controls → Rey blade), mirroring the extension's
             .floating-sidebar-nav__divider. Uses the theme-aware --border-separator token so it
             reads correctly in light and dark; width matches the 40px blades. -->
        <div v-if="has_spark" class="w-10 h-px bg-border-separator my-2" />
        <!-- Rey (Spark AI) blade — styled to match the Chrome-extension .rey-btn: blue Rey-brand
             gradient at rest, light gradient on hover only, and the Rey glyph tinted via currentColor
             (white at rest, dark on hover). NOTE: the extension's `.rey-btn.active` rules are dead
             code there — it flips a `data-active` attribute and never adds the `.active` class — so
             the real extension blade stays blue while active and lightens only on hover. We match
             that: there is deliberately no active-state background change. The gradient rules live in
             the scoped <style> block below. Tooltip reads "Rey" (the extension's display name); the
             internal tab id stays 'Spark' so tab logic and analytics are unchanged. -->
        <div v-if="has_spark" class="rey-blade w-10 h-10 cursor-pointer flex items-center justify-center rounded-xl my-2 relative" title="Rey" @click="setActiveTab('Spark')">
          <span class="inline-flex" v-html="spark_icon" />
          <div v-if="spark_show_unread_indicator" :class="red_indicator_class" />
        </div>
        <!-- Section divider (Rey blade → main blades), mirroring the extension's second
             .floating-sidebar-nav__divider. Shown only when a main blade follows it. -->
        <div v-if="has_spark && (has_contact || has_internal_messaging || has_autovision)" class="w-10 h-px bg-border-separator my-2" />
        <div v-if="has_contact" class="w-10 h-10 cursor-pointer flex items-center justify-center rounded-xl my-2" :title="$t('common.contact')" @click="setActiveTab('Contact')"
             :class="active_tab === 'Contact'?'bg-brand-subtle':'bg-brand-subtle-inactive hover:bg-surface-3'">
          <span class="text-icon-fill inline-flex" v-html="customer_profile_icon" />
        </div>
        <div v-if="has_conversations" class="w-10 h-10 cursor-pointer flex items-center justify-center rounded-xl my-2" :title="$t('common.conversations')" @click="setActiveTab('Conversations')"
             :class="active_tab === 'Conversations'?'bg-brand-subtle':'bg-brand-subtle-inactive hover:bg-surface-3'">
          <span class="text-icon-fill inline-flex" v-html="conversations_icon" />
        </div>
        <div v-if="has_internal_messaging" class="w-10 min-h-[40px] py-1 cursor-pointer flex flex-col items-center justify-center gap-0.5 rounded-xl my-2" :title="$t('common.internalMessaging')" @click="setActiveTab('InternalMessaging')"
             :class="active_tab === 'InternalMessaging'?'bg-brand-subtle':'bg-brand-subtle-inactive hover:bg-surface-3'">
          <!-- Icon + count pill stacked to match the Chrome-extension blade layout (flex column, 2px
               gap, centered, min-h aligned with the other 40px tabs). The pill's colors and shape
               mirror the extension's .icon-btn__count exactly: #343842 fill, white text, 16px radius,
               min-width 20px, 2px/4px padding, 12px/400 text. -->
          <!-- Icon sits in a fixed 24px-tall centered box (h-6), mirroring the extension's
               .icon-btn>img { width:24px; height:24px }. The glyph SVG is only 20px tall (viewBox
               0 0 24 20); without the fixed box the span collapses to 20px and the icon rides up
               to the top of the stack once the count pill is added. The 24px box keeps the ~2px of
               space above the glyph so it stays vertically balanced with the pill, exactly like the blade. -->
          <span class="text-icon-fill inline-flex items-center justify-center h-6" v-html="internal_messages_icon" />
          <div v-if="internal_messages_unread_badge"
               class="min-w-[20px] px-1 py-0.5 flex items-center justify-center rounded-2xl bg-[#343842] text-white text-[12px] font-normal leading-none">
            {{ internal_messages_unread_badge }}
          </div>
        </div>
        <div v-if="has_autovision" class="w-10 h-10 cursor-pointer flex items-center justify-center rounded-xl my-2" title="AutoVision" @click="setActiveTab('AutoVision')"
             :class="active_tab === 'AutoVision'?'bg-brand-subtle':'bg-brand-subtle-inactive hover:bg-surface-3'">
          <img :src="auto_vision_icon" alt="AutoVision" width="24" />
        </div>

        <div v-if="user_access_info" class="options-menu-trigger mt-auto cursor-pointer highlight-hover pb-3" :title="$t('toolbar.options')" @click="updateOptionsMenuTrigger">
          <svg class="text-text-default mb-2" style="width:1.75rem;height:1.75rem" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  textarea {
    width: 100%;
    white-space: pre; /* Preserve spaces and line breaks */
    background-color: var(--background-layer-02);
    border: 1px solid var(--background-layer-02);
    height: 200px;
  }

  /* Rey (Spark AI) blade — mirrors the Chrome-extension .rey-btn so the standalone toolbar matches
     the extension. These are fixed Rey-brand gradients (identical in light and dark, exactly as the
     extension paints them), so they are intentionally hardcoded rather than theme tokens — the one
     documented exception to the "use tokens" rule for this branded button. (#9CBAFC is brand-blue-30;
     #0A5DFF / #367AFF / #E9F0FF are not design tokens.) The Rey glyph SVG uses currentColor, so the
     `color` here tints it: white at rest, near-black on hover. Hover only — the extension's active
     state does not repaint this blade (see the template comment), so neither does ours. */
  .rey-blade {
    background: linear-gradient(180deg, #0A5DFF 0%, #367AFF 100%);
    color: #fff;
  }
  .rey-blade:hover {
    background: linear-gradient(218.73deg, #9CBAFC 23.06%, #E9F0FF 138.18%);
    color: #0B101E;
  }
</style>
<style>
  [data-theme="dark"] .rr-logo {
    filter: brightness(0) invert(1);
  }
</style>
<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import FusionBody from '@components/Core/FusionBody.vue';
import OptionsMenu from '@components/Settings/OptionsMenu.vue';
import MessageToast from '@components/Core/MessageToast.vue';
import { isTrustedHostMessage } from '../../utils/embedOrigins';
import RrLogo from '@images/RRLogo.svg?raw';
import ExpandIconRaw from '@images/expand_icon.svg?raw';
import CollapseIconRaw from '@images/collapse_icon.svg?raw';
import SparkIconRaw from '@images/spark_icon.svg?raw';
import CustomerProfileIconRaw from '@images/customer_profile_icon.svg?raw';
import ConversationsIconRaw from '@images/conversations_icon.svg?raw';
import InternalMessagesIconRaw from '@images/internal_messages_icon.svg?raw';
import AutoVisionIcon from '@images/auto_vision_40x40.jpg';

// Hosts that embed the toolbar in a plain <iframe> and do NOT resize that frame when we post
// COLLAPSE-FUSION outward. Collapsing hides .left-column (display:none, app-fusion.scss) on the
// assumption the host shrinks its iframe down to the 64px tab rail — the way ERA does. A host that
// ignores the message leaves the frame at full width, so the rail lands flush against the left edge
// and the vacated area paints as empty page background. For these systems the collapse affordance is
// suppressed entirely rather than left to produce a broken layout. Once a host implements the resize,
// drop it from this list. Matched case-insensitively against FusionInitType.InitSystem.
const NON_COLLAPSIBLE_INIT_SYSTEMS = ['ENHANCEDFOCUS'];

export default {
  props: {
    setConfiguration: {
      type: Object,
      required: false,
      default: null
    },
    setToken: {
      type: String,
      required: false,
      default: null
    },
    authState: {
      type: String,
      required: false,
      default: 'ready',
      validator: (value) => ['loading', 'connecting', 'unauthorized', 'ready'].includes(value)
    },
  },
  components: {
    'fusion-body': FusionBody,
    'options-menu': OptionsMenu,
    'message-toast': MessageToast,
  },
  created() {
    // Dev shim: VITE_DEFAULT_TAB lets a developer force the initial tab for local testing
    // (e.g. 'InternalMessaging'). Honored only when the value matches a known page tab.
    // Gated on `import.meta.env.DEV` — Vite's build-time flag, true under `npm run dev` and
    // false in production builds — so a stray VITE_DEFAULT_TAB leaking into a prod build
    // pipeline can't lock every user onto a forced tab. The dead-code branch tree-shakes out
    // of prod bundles.
    const overrideTab = import.meta.env.DEV ? import.meta.env.VITE_DEFAULT_TAB : null;
    if(overrideTab && this.page_tabs.hasOwnProperty(overrideTab))
      this.active_tab = overrideTab;
    else
      this.active_tab = this.debug_mode ? 'Reynolds' : 'Spark';

    if (this.authState === 'ready')
      this.initializeWhenReady();
  },
  mounted() {
    window.addEventListener('message', this.handleWindowMessage);
    // Surface an in-app toast when an internal message lands while the user is on the Spark
    // (AI) tab. FusionToolbar is the single toolbar instance and already owns active_tab, so
    // it's the natural single-fire listener — unlike MessengerWindow, which is mounted twice
    // (Spark + InternalMessaging bodies) and would double-fire off the same WS-MESSAGE.
    window.event_bus.$on('WS-MESSAGE', this.handleWsMessageForToast);
    // Auto-navigate the user into a newly created support (TAC) chat. When a send-to-human /
    // get-me-a-human handoff creates the support thread, the backend sends this user a Web PubSub
    // 'invite' push; ride it here — again the single toolbar instance that owns active_tab — to
    // switch to the InternalMessaging tab and open the thread, so users don't have to hunt for it.
    window.event_bus.$on('WS-MESSAGE', this.handleWsMessageForSupportJump);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handleWindowMessage);
    window.event_bus.$off('WS-MESSAGE', this.handleWsMessageForToast);
    window.event_bus.$off('WS-MESSAGE', this.handleWsMessageForSupportJump);
    this.dismissMessageToast();
    this.mainStore.endReySession();
  },
  data() {
    return {
      rr_logo: RrLogo,
      rr_logo_raw: RrLogo,
      expand_icon: ExpandIconRaw,
      collapse_icon: CollapseIconRaw,
      spark_icon: SparkIconRaw,
      customer_profile_icon: CustomerProfileIconRaw,
      conversations_icon: ConversationsIconRaw,
      internal_messages_icon: InternalMessagesIconRaw,
      auto_vision_icon: AutoVisionIcon,
      fusion_collapsed: null,
      active_tab: null,
      page_tabs: {
        Reynolds: 'Reynolds',
        Spark: 'Spark',
        Contact: 'Contact',
        Conversations: 'Conversations',
        InternalMessaging: 'InternalMessaging',
      },
      sent_message: null,
      received_message: null,
      options_menu_trigger: 0,
      spark_has_unreads: null,
      int_msg_has_unreads: null,
      thread_id_to_last_seen_thread_msg_id: {},
      // Rey (Spark) blade unread dot — mirrors the extension's .rey-btn--notify::after
      // (8px, 4px inset from top/right, #FF5C7E). Only the Spark blade uses this.
      red_indicator_class: 'w-2 h-2 top-1 right-1 absolute bg-[#FF5C7E] rounded-full',
      pending_iframe_context_request: false,
      // Incoming-internal-message toast (shown only while on the Spark/AI tab).
      // Shape: { title: String, preview: String } or null when hidden.
      toast_message: null,
      toast_timer_id: null,
      toast_duration_ms: 6000,
      autovision_web_base_uri: import.meta.env.VITE_AUTOVISION_WEB_BASE_URI,
      // Prevents initializeWhenReady() from running more than once (it can be reached
      // from both created() and the authState watcher).
      is_ready_initialized: false,
    }
  },
  computed: {
    ...mapStores(useMainStore),
    wingman_config() {
      return this.mainStore.wingman_config;
    },
    dealer_guid() {
      return this.mainStore.active_dealer?.companyId || null;
    },
    rr_logo_icon() {
      // Rename gradient ID to avoid collision with the large logo's identical gradient ID
      return this.rr_logo_raw
        .replaceAll('New_Gradient_Swatch_4', 'New_Gradient_Swatch_4_sm');
    },
    internal_messages_unread_count() {
      return this.mainStore.internal_messages_unread_count;
    },
    spark_unread_count() {
      return this.mainStore.spark_unread_count;
    },
    threads_type_by_thread_id() {
      return this.mainStore.threads_type_by_thread_id;
    },
    has_chat_access() {
      return this.mainStore.has_chat_access;
    },
    has_tac_access() {
      return this.mainStore.has_tac_access;
    },
    enterprise_users_data() {
      return this.mainStore.enterprise_users_data;
    },
    // personGuid -> enterprise user object, mirroring MessengerWindow's ent_users_by_guid. Used to
    // resolve a sender's display name for the incoming-message toast (falls back to a deterministic
    // ghost name when the sender isn't in the loaded enterprise list).
    ent_users_by_guid() {
      return this.$FUSarrayOfObjectsKeyMap(this.enterprise_users_data?.enterpriseUsers || [], 'personGuid') || {};
    },
    // Badge label for the Internal Messaging tab icon. Empty string hides the badge; counts
    // above 9 collapse to '9+'. Visibility matches the prior red-dot semantics (count > 0).
    internal_messages_unread_badge() {
      const count = this.internal_messages_unread_count;
      if (!count || count < 1) return '';
      return count > 9 ? '9+' : String(count);
    },
    debug_mode() {
      return this.wingman_config?.Debug || false;
    },
    user_access_info() {
      return this.mainStore.user_access_info;
    },
    current_user_guid() {
      return this.user_access_info?.userId || null;
    },
    can_have_channels() {
      return Boolean(this.dealer_guid);
    },
    config_fusion_init_type() {
      return this.wingman_config?.FusionInitType || null;
    },
    config_allowed_fuses() {
      return this.wingman_config?.AllowedFuses || [];
    },
    fuse_configurations() {
      return this.setConfiguration?.FuseConfigurations || {};
    },
    config_init_system() {
      return this.config_fusion_init_type?.InitSystem || '';
    },
    current_cif() {
      return this.mainStore.current_cif;
    },
    channel_threads: {
      get() {
        return this.mainStore.channel_threads;
      },
      set(value) {
        this.mainStore.channel_threads = value;
      }
    },
    user_threads: {
      get() {
        return this.mainStore.user_threads;
      },
      set(value) {
        this.mainStore.user_threads = value;
      }
    },
    object_threads: {
      get() {
        return this.mainStore.object_threads;
      },
      set(value) {
        this.mainStore.object_threads = value;
      }
    },
    channel_threads_processing: {
      get() {
        return this.mainStore.channel_threads_processing;
      },
      set(value) {
        this.mainStore.channel_threads_processing = value;
      }
    },
    user_threads_processing: {
      get() {
        return this.mainStore.user_threads_processing;
      },
      set(value) {
        this.mainStore.user_threads_processing = value;
      }
    },
    object_threads_processing: {
      get() {
        return this.mainStore.object_threads_processing;
      },
      set(value) {
        this.mainStore.object_threads_processing = value;
      }
    },
    spark_thread_id() {
      return this.mainStore.ai_thread_obj?.id || null;
    },
    config_spark_ai_chat() {
      if(this.$FUSarrayHasEntries(this.config_allowed_fuses)) {
        for(let i=0; i<this.config_allowed_fuses.length; i++) {
          if(this.config_allowed_fuses[i].System === 'AVMessenger' && this.config_allowed_fuses[i].Function === 'SparkAIChat')
            return this.config_allowed_fuses[i];
        }
      }
      
      return null;
    },
    config_spark_ai_module_url() {
      return this.config_spark_ai_chat?.ModuleUrl || null;
    },
    spark_body_config() {
      return { PageType: 'Spark', FuseConfigurations: { ...this.fuse_configurations, threadId: this.spark_thread_id } };
    },
    contact_body_config() {
      return { PageType: 'ContactDetails', FuseConfigurations: {
        ...this.fuse_configurations,
        ...this.setConfiguration?.ContactFuseConfigurations || {}
      }};
    },
    internal_messaging_body_config() {
      return { PageType: 'InternalMessaging', FuseConfigurations: { ...this.fuse_configurations } };
    },
    conversations_body_config() {
      return { PageType: 'Conversations', FuseConfigurations: { ...this.fuse_configurations } };
    },
    fuses_by_system_function() {
      let fieldData = {};
      if(this.$FUSarrayHasEntries(this.config_allowed_fuses)) {
        this.config_allowed_fuses.forEach(fuse => {
          let fuseKey = `${fuse.System}_${fuse.Function}`;
          fieldData[fuseKey] = fuse;
        });
      }
      
      return fieldData
    },
    show_close() {
      const initSystemId = this.config_init_system;
      return typeof initSystemId === 'string' ? initSystemId.toLowerCase().includes('sting') : false;
    },
    // False for hosts listed in NON_COLLAPSIBLE_INIT_SYSTEMS (see that const for why). Gates every
    // path into the collapsed state: the chevron, the RR-logo toggle, re-clicking the already-active
    // tab, and an inbound COLLAPSE-FUSION from the host. An unknown or missing InitSystem stays
    // collapsible, so this is opt-in per host and no existing integration changes behavior.
    can_collapse() {
      const initSystemId = this.config_init_system;
      if (typeof initSystemId !== 'string' || !initSystemId) return true;
      return !NON_COLLAPSIBLE_INIT_SYSTEMS.includes(initSystemId.trim().toUpperCase());
    },
    has_spark() {
      return Boolean(this.current_cif);
    },
    has_fusion_entitlement() {
      return this.mainStore.has_fusion_entitlement;
    },
    has_internal_messaging() {
      return Boolean(this.current_cif);
    },
    has_conversations() {
      return false;
    },
    has_autovision_access() {
      return this.mainStore.has_autovision_access;
    },
    has_autovision() {
      return this.has_autovision_access && Boolean(this.vin) && Boolean(this.autovision_iframe_url);
    },
    autovision_fuse() {
      if (this.$FUSarrayHasEntries(this.config_allowed_fuses))
        return this.config_allowed_fuses.find(fuse => fuse.System === 'AutoVision') || null;
      
      return null;
    },
    autovision_fuse_payload() {
      return this.autovision_fuse?.Payload || '';
    },
    vin() {
      if(this.$FUSisValidVin(this.autovision_fuse_payload))
        return this.autovision_fuse_payload.toUpperCase();
      
      return null;
    },
    autovision_vin_host() {
      //TODO: THIS IS USING LEGACY FIELDS. NEED TO UPDATE
      if (!this.config_spark_ai_module_url) return '';
      try {
        const url = new URL(this.config_spark_ai_module_url);
        return url.host || '';
      } catch (e) {
        return '';
      }
    },
    autovision_vin_url_encoded() {
      if (!this.config_spark_ai_module_url) return '';
      try {
        return btoa(this.config_spark_ai_module_url);
      } catch (e) {
        return '';
      }
    },
    autovision_auction_listing_hash() {
      // Format: S:[SOURCE]V:[VIN], URL encoded
      if (!this.vin) return '';
      const hash = `S:[${this.config_init_system}]V:[${this.vin}]`;
      return encodeURIComponent(hash);
    },
    autovision_iframe_url() {
      if (!this.vin || !this.dealer_guid || !this.autovision_web_base_uri)
        return null;
      
      const basePath = `${this.autovision_web_base_uri.replace(/\/$/, '')}/app/${this.dealer_guid}/auction_eval`;
      const params = new URLSearchParams({
        tool_type: 'ReturnIfBooked',
        app_class: 'widget-app',
        vin_source: this.config_init_system,
        vin_host: this.autovision_vin_host,
        vin_url_encoded: this.autovision_vin_url_encoded,
        vin_number: this.vin,
        auction_listing_hash: this.autovision_auction_listing_hash,
        decode_failed: '',
        fkey: '',
        in_live_sale: 'false',
        extension_id: ''
      });
      
      return `${basePath}?${params.toString()}`;
    },
    iframe_key_autovision() {
      return `iframe_autovision_${this.dealer_guid}_${this.autovision_fuse_vin}`;
    },
    has_contact() {
      const fc = this.fuse_configurations;
      return Boolean(this.current_cif) && Boolean(fc.cdpId || fc.email || fc.phone);
    },
    unreads_by_thread_id: {
      get() {
        return this.mainStore.unreads_by_thread_id;
      },
      set(value) {
        this.mainStore.unreads_by_thread_id = value;
      }
    },
    spark_show_unread_indicator() {
      return this.spark_has_unreads === null?this.spark_unread_count>0:this.spark_has_unreads;
    },
    int_msg_show_unread_indicator() {
      return this.int_msg_has_unreads === null?this.internal_messages_unread_count>0:this.int_msg_has_unreads;
    },
    rey_session_id() {
      return this.mainStore.rey_session_id;
    },
  },
  methods: {
    handleUnauthorizedClick() {
      window.event_bus.$emit('UNAUTHORIZED-CLICK');
    },
    initializeWhenReady() {
      if (this.is_ready_initialized) return;
      this.is_ready_initialized = true;

      // Initialise the IndexedDB analytics module before starting the session so the first
      // enqueue has platform, userId, and dealershipId baked in. Chain everything so the
      // rey_session_id is resolved (including any sibling-iframe adoption) before API calls
      // that need X-Rey-Session-Id fire.
      const self = this;
      this.mainStore.initAnalytics()
        .then(() => self.mainStore.startReySession())
        .then(() => {
          if (self.setToken) {
            if (!self.object_threads_processing && !self.$FUSarrayHasEntries(self.object_threads)) self.getObjectThreads();
            if (!self.user_threads_processing && !self.$FUSarrayHasEntries(self.user_threads)) self.getUserThreads();
            if (!self.channel_threads_processing && !self.$FUSarrayHasEntries(self.channel_threads)) self.getChannels();
          } else {
            self.$FUSlog('FusionToolbar: No auth token generated', 'warn');
          }
        });
    },
    handleWindowMessage(ev) {
      let receivedMessage = ev?.data || null;
      let messageType = receivedMessage?.type || null;
      let messageData = receivedMessage?.data || null;
      let messageSource = receivedMessage?.source || '';
      let messageKey = receivedMessage?.key || '';
      if(messageSource.indexOf('vue-devtools')>=0 || messageSource.indexOf('proxy')>=0 || messageKey.indexOf('_VUE_')>=0)
        return;
      // FUS-474: only a host above us, from an allowed origin.
      if(messageSource.indexOf('FUSION')<0 && !isTrustedHostMessage(ev))
        return;
      if(messageSource.indexOf('FUSION')<0) {
        this.received_message = receivedMessage;
        switch (messageType) {
          case 'COLLAPSE-FUSION':
            // Ignored for non-collapsible hosts (see can_collapse). Such a host has effectively told
            // us it does not resize its frame, so honoring a collapse request from it would produce
            // exactly the broken layout this gate exists to prevent. No analytics either — nothing
            // collapsed, so there is no collapse to record.
            if (!this.can_collapse) break;
            window.Analytics?.enqueue('toolbar_item_clicked', { session_id: this.rey_session_id, item_id: 'ext_collapse_fusion', item_label: 'Collapse (External)', triggered_by: 'external' });
            this.fusion_collapsed = messageData;
            break;
          case 'ACTIVE-TAB':
            window.Analytics?.enqueue('toolbar_item_clicked', { session_id: this.rey_session_id, item_id: 'ext_active_tab', item_label: `${messageData} (External)`, triggered_by: 'external' });
            this.active_tab = this.page_tabs.hasOwnProperty(messageData) ? messageData : this.active_tab;
            break;
          case 'SHOW-OPTIONS':
            this.options_menu_trigger = Date.now();
            break;
        }
      }
    },
    expandCollapse(fusionCollapse, triggeredBy = 'user') {
      // Non-collapsible hosts never enter the collapsed state (see can_collapse). The chevron is
      // already hidden for them, but the RR-logo toggle in setActiveTab also lands here, so the guard
      // belongs on the method rather than only on the button. Nothing is posted outward either: a
      // host that cannot collapse should not be told that it did.
      if (!this.can_collapse) return;
      // triggeredBy defaults to 'user' (a chevron click); programmatic callers (e.g. the support-chat
      // auto-jump expanding a collapsed panel) pass 'system' so analytics don't misattribute it as a
      // manual collapse/expand. Mirrors setActiveTab.
      if (fusionCollapse)
        window.Analytics?.enqueue('toolbar_item_clicked', { session_id: this.rey_session_id, item_id: this.show_close ? 'btn_close' : 'btn_collapse', item_label: this.show_close ? 'Close' : 'Collapse', triggered_by: triggeredBy });
      else
        window.Analytics?.enqueue('toolbar_item_clicked', { session_id: this.rey_session_id, item_id: 'btn_expand', item_label: 'Expand', triggered_by: triggeredBy });
      this.fusion_collapsed = Boolean(fusionCollapse);
      this.sendWindowMessage('COLLAPSE-FUSION', fusionCollapse);
    },
    resetMessengerSession() {
      // Wrapper around the Pinia action — clears InternalMessaging transient state
      // (search + open-contact + pending-recipient) on tab leave.
      this.mainStore.ResetMessengerSession();
    },
    handleWsMessageForToast(data) {
      // Only surface a toast while the user is actively in the Spark (AI) tab — that's the whole
      // point: let them know an internal message arrived without leaving their AI conversation.
      if (this.active_tab !== 'Spark') return;
      if (!data || data.type !== 'message') return;
      const senderId = data.senderId || '';
      // Ignore our own messages and system/AI senders (Spark responses, status bots, etc.).
      if (!senderId || senderId === this.current_user_guid) return;
      if (senderId.indexOf(this.$FUSsystemUserPrefix) >= 0) return;
      const threadId = data.threadId || null;
      if (!threadId) return;
      // Classify the thread. This toast is intentionally scoped to Support (TACChat) chats ONLY —
      // DMs, channels, object threads, and Spark (SystemAI_) threads are all deliberately excluded,
      // so anything that isn't a TACChat thread is skipped. An unknown thread type (brand-new thread
      // not yet loaded) is likewise skipped, since it won't classify as TACChat until the type map
      // is populated. Support access is still required to toast a TACChat message.
      const threadType = this.threads_type_by_thread_id[threadId] || '';
      if (threadType !== 'TACChat') return;
      if (!this.has_tac_access) return;
      // Dedupe: the same WS-MESSAGE can reach this handler more than once — e.g. a duplicate
      // event-bus subscription left over from an HMR remount, or repeated socket delivery — which
      // would surface two identical toasts. Ignore a message already toasted in the last few seconds.
      // Keyed by thread + message id, so genuinely distinct messages still each toast.
      const toastKey = `${threadId}:${data.threadMsgId ?? ''}`;
      const now = Date.now();
      if (!this._recent_toast_keys) this._recent_toast_keys = new Map();
      for (const [k, ts] of this._recent_toast_keys) {
        if (now - ts > 4000) this._recent_toast_keys.delete(k);
      }
      if (this._recent_toast_keys.has(toastKey)) return;
      this._recent_toast_keys.set(toastKey, now);
      this.showMessageToast(data);
    },
    handleWsMessageForSupportJump(data) {
      // A support (TAC) chat was just created for this user — e.g. at the end of a Rey
      // send-to-human / get-me-a-human handoff — so drop them straight into that conversation on the
      // Internal Messaging screen. Users were losing track of where the support chat went after the
      // handoff; this removes the hunt. Handled here (not in MessengerWindow) because FusionToolbar
      // is the single instance that owns active_tab, while MessengerWindow is mounted twice.
      if (!data || data.type !== 'invite') return;
      // Backend tags TAC-chat invites with threadType 'Support' (TacChatInitiationService →
      // UserThreadCreatedNotification(..., "Support")). Only those trigger the jump — DM, channel,
      // and object invites must never yank the user anywhere.
      if (data.threadType !== 'Support') return;
      const threadId = data.threadId || null;
      if (!threadId) return;
      // Only navigate if this user actually has the Internal Messaging + TAC surfaces.
      if (!this.has_internal_messaging || !this.has_tac_access) return;
      // Dedupe duplicate WS delivery (see handleWsMessageForToast) so a repeated invite for the same
      // thread doesn't re-fire the jump.
      const now = Date.now();
      if (!this._recent_support_jump_ids) this._recent_support_jump_ids = new Map();
      for (const [k, ts] of this._recent_support_jump_ids) {
        if (now - ts > 4000) this._recent_support_jump_ids.delete(k);
      }
      if (this._recent_support_jump_ids.has(threadId)) return;
      this._recent_support_jump_ids.set(threadId, now);
      // Switch to the InternalMessaging tab first. Entering the tab runs resetMessengerSession()
      // (clears internal_messaging_state / active_thread_id), so the open MUST come after — hence the
      // $nextTick below. Guard the switch: calling setActiveTab with the tab already active would
      // collapse the panel instead of staying put.
      // That skipped switch is also what expands the panel, though — so when we're already on the tab
      // and collapsed, expand explicitly, otherwise the thread opens behind a collapsed toolbar and
      // the whole handoff looks like it did nothing.
      if (this.active_tab !== 'InternalMessaging')
        this.setActiveTab('InternalMessaging', 'system');
      else if (this.fusion_collapsed)
        this.expandCollapse(false, 'system');
      // Open the thread over the existing in-app channel. openThreadFromEvent joins/loads by id even
      // when the thread isn't in the list yet, and is scoped to the InternalMessaging MessengerWindow
      // instance so the hidden Spark window ignores it.
      this.$nextTick(() => {
        window.event_bus.$emit('MESSENGER-OPEN-THREAD', threadId);
      });
    },
    // Resolve the sender's display name from the message's senderId. Prefer the enterprise user's
    // fullName (first + last), falling back to friendlyName (first name only) when fullName is
    // absent, then to a deterministic ghost name (itself a two-word "First Last"-style pseudonym).
    // Used for the toast title so a DM reads as the person who messaged rather than the thread name
    // (which is "Unnamed Thread" for DMs).
    resolveSenderName(data) {
      const senderId = data?.senderId || '';
      if (!senderId) return '';
      const entUser = this.ent_users_by_guid[senderId];
      if (entUser) return entUser.fullName || entUser.friendlyName || '';
      return this.$FUSresolveGhostUser(senderId).friendlyName;
    },
    showMessageToast(data) {
      const title = data.senderName || data.name || this.resolveSenderName(data) || 'New message';
      this.toast_message = { title: title, preview: this.buildToastPreview(data.content) };
      if (this.toast_timer_id) clearTimeout(this.toast_timer_id);
      const self = this;
      this.toast_timer_id = setTimeout(() => { self.dismissMessageToast(); }, this.toast_duration_ms);
    },
    dismissMessageToast() {
      this.toast_message = null;
      if (this.toast_timer_id) {
        clearTimeout(this.toast_timer_id);
        this.toast_timer_id = null;
      }
    },
    buildToastPreview(content) {
      // Strip basic markdown and collapse whitespace so the preview reads as plain text, then
      // cap the length so the toast stays compact.
      if (!content || typeof content !== 'string') return '';
      const plain = content
        .replace(/#{1,6}\s*/g, '')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .replace(/\n+/g, ' ')
        .trim();
      return plain.length > 90 ? plain.slice(0, 90).trimEnd() + '…' : plain;
    },
    setActiveTab(activeTab, triggeredBy = 'user') {
      // Conversations omitted: has_conversations is hardcoded false and the tab never renders.
      const _tabTrack = {
        Spark:             { item_id: 'tab_spark',              item_label: 'Spark AI' },
        InternalMessaging: { item_id: 'tab_internal_messaging', item_label: 'Internal Messaging' },
        Contact:           { item_id: 'tab_contact',            item_label: 'Contact' },
        AutoVision:        { item_id: 'tab_autovision',         item_label: 'AutoVision' },
      };
      const _t = _tabTrack[activeTab];
      // triggeredBy defaults to 'user' (a blade click); programmatic callers (e.g. the support-chat
      // auto-jump) pass 'system' so analytics don't misattribute the switch as a manual tab click.
      if (_t) window.Analytics?.enqueue('toolbar_item_clicked', { session_id: this.rey_session_id, ..._t, triggered_by: triggeredBy });
      // Capture the outgoing tab before any mutation so we can detect leaving/entering
      // InternalMessaging. FusionBody instances stay mounted (v-show) across tab switches, so
      // component-level beforeUnmount/setMessagingState resets don't fire here — we have to
      // clear at this seam. Reset on both enter and leave so background thread auto-opens
      // (e.g. from a thread_id URL param) don't bleed into the navbar on first visit.
      const _leavingInternalMessaging = this.active_tab === 'InternalMessaging' && activeTab !== 'InternalMessaging';
      const _enteringInternalMessaging = activeTab === 'InternalMessaging' && this.active_tab !== 'InternalMessaging';
      if((activeTab==='Reynolds' && !this.debug_mode))
        this.expandCollapse(!Boolean(this.fusion_collapsed));
      else {
        // Re-clicking the already-active blade collapses the panel — the Rey blade is the common
        // case, but every blade shares this path. Non-collapsible hosts (see can_collapse) get a
        // pure no-op instead: the guard is nested rather than folded into the condition above so the
        // click does NOT fall through to the else branch, which would post a redundant ACTIVE-TAB +
        // COLLAPSE-FUSION:false pair at the host on every re-click.
        if(this.active_tab === activeTab && !this.fusion_collapsed && !this.show_close) {
          if(this.can_collapse) {
            this.fusion_collapsed = Boolean(true);
            this.sendWindowMessage('COLLAPSE-FUSION', true);
          }
        }
        else {
          this.active_tab = activeTab;
          this.sendWindowMessage('ACTIVE-TAB', activeTab);
          this.sendWindowMessage('COLLAPSE-FUSION', false);
          this.fusion_collapsed = Boolean(false);
        }
      }
      if(_leavingInternalMessaging || _enteringInternalMessaging)
        this.resetMessengerSession();
    },
    sendWindowMessage(type, data=null) {
      this.sent_message = {
        type: type,
        data: data,
        source: 'FUSION'
      };
      this.$FUSpostFusionMessage(type, data, 'FUSION');
    },
    getChannels() {
      if(this.can_have_channels) {
        const self = this;
        this.channel_threads_processing = true;
        const dealerThreadDTO = `dealer/${this.dealer_guid}`;
        this.mainStore.GetThreads(dealerThreadDTO).then(response => {
          self.channel_threads = self.$FUSarrayHasEntries(response)?response:[];
          self.channel_threads.forEach(x => {
            if(x.threadId) {
              let unreadCount = x.unreadCount || 0;
              // Server unreadCount is the absolute count for this thread — assign, never accumulate.
              // A targeted refetch (reconnect, context change) skips any map reset, so a `+=` here
              // would stack the server value onto the existing count and double it.
              self.unreads_by_thread_id[x.threadId] = unreadCount;
              self.thread_id_to_last_seen_thread_msg_id[x.threadId] = x.lastSeenThreadMsgId;
            }
          });
          self.channel_threads_processing = false;
        }, error => {
          self.channel_threads_processing = false;
        });
      }
    },
    getObjectThreads() {
      const self = this;
      const recentThreadDTO = `user/objectThreads`;
      this.object_threads_processing = true;
      this.mainStore.GetThreads(recentThreadDTO).then(response => {
        self.object_threads = self.$FUSarrayHasEntries(response)?response:[];
        self.object_threads.forEach(x => {
          if(x.threadId) {
            let unreadCount = x.unreadCount || 0;
            // Server unreadCount is the absolute count for this thread — assign, never accumulate.
            // A targeted refetch (reconnect, context change) skips any map reset, so a `+=` here
            // would stack the server value onto the existing count and double it.
            self.unreads_by_thread_id[x.threadId] = unreadCount;
            self.thread_id_to_last_seen_thread_msg_id[x.threadId] = x.lastSeenThreadMsgId;
          }
        });
        self.object_threads_processing = false;
      }, error => {
        self.object_threads_processing = false;
      });
    },
    getUserThreads() {
      const self = this;
      this.user_threads_processing = true;
      const userThreadDTO = `user/${this.current_user_guid}`;
      this.mainStore.GetThreads(userThreadDTO).then(response => {
        self.user_threads = self.$FUSarrayHasEntries(response)?response:[];
        self.user_threads.forEach(x => {
          if(x.threadId) {
            let unreadCount = x.unreadCount || 0;
            // Server unreadCount is the absolute count for this thread — assign, never accumulate.
            // A targeted refetch (reconnect, context change) skips any map reset, so a `+=` here
            // would stack the server value onto the existing count and double it.
            self.unreads_by_thread_id[x.threadId] = unreadCount;
            self.thread_id_to_last_seen_thread_msg_id[x.threadId] = x.lastSeenThreadMsgId;
          }
        });
        self.user_threads_processing = false;
      }, error => {
        self.user_threads_processing = false;
      });
    },
    updateOptionsMenuTrigger() {
      window.Analytics?.enqueue('toolbar_item_clicked', { session_id: this.rey_session_id, item_id: 'btn_options', item_label: 'Options', triggered_by: 'user' });
      this.options_menu_trigger = Date.now();
    },
    updateShowOptions(showOptions) {
      this.sendWindowMessage('SHOW-OPTIONS', showOptions);
      if(showOptions) {
        this.fusion_collapsed = Boolean(false);
        this.sendWindowMessage('COLLAPSE-FUSION', false);
      }
    },
    // A scheduled result opened from the Schedules inbox (OptionsMenu). The menu is reachable from
    // every tab and the result is a Rey conversation, so move to Spark first — guarded the way
    // handleWsMessageForSupportJump guards its switch, because setActiveTab on the tab that is already
    // open collapses the panel instead. No $nextTick: leaving another tab resets nothing the Spark
    // panel reads, and the store holds the request for a Spark panel that is not mounted yet.
    openReyConversation(threadId) {
      if (!threadId || !this.has_spark) return;
      if (this.active_tab !== 'Spark')
        this.setActiveTab('Spark', 'system');
      else if (this.fusion_collapsed)
        this.expandCollapse(false, 'system');
      this.requestSparkConversationOpen(threadId);
    },
    // Wrapper: ask the Spark panel to open one Rey conversation.
    requestSparkConversationOpen(threadId) {
      this.mainStore.RequestSparkConversationOpen(threadId);
    },
  },
  watch: {
    authState(newValue) {
      if (newValue === 'ready')
        this.initializeWhenReady();
    },
    // A non-collapsible host's wingmanConfig arrives asynchronously (UPDATE-CONTEXT), and the chevron
    // is live before it lands — so a user can collapse the panel during the handshake and then have
    // the chevron disappear from under them, stranding the toolbar collapsed with no way to reopen it.
    // Force it back open the moment the host is known to be non-collapsible. Sets state directly
    // rather than calling expandCollapse(), which now short-circuits on !can_collapse; the outbound
    // COLLAPSE-FUSION:false still goes out so a host that did shrink its frame restores it.
    can_collapse(newValue) {
      if (newValue || !this.fusion_collapsed) return;
      this.fusion_collapsed = Boolean(false);
      this.sendWindowMessage('COLLAPSE-FUSION', false);
    },
  },
}</script>
